import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiInstance from '../../utils/axios';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../utils/constants';

function Checkout() {
    const [order, setOrder] = useState(null);
    const [couponCode, setCouponCode] = useState("");
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [showCoupon, setShowCoupon] = useState(false);
    const param = useParams();

    // Helper function to safely format numbers
    const formatPrice = (value) => {
        if (typeof value === 'number') {
            return value.toFixed(2);
        }
        if (typeof value === 'string') {
            const num = parseFloat(value);
            return isNaN(num) ? '0.00' : num.toFixed(2);
        }
        return '0.00';
    };

    const fetchOrderData = () => {
        apiInstance.get(`checkout/${param?.order_oid}/`)
            .then((res) => {
                // Ensure numeric fields are properly formatted
                const formattedOrder = {
                    ...res.data,
                    sub_total: formatPrice(res.data.sub_total),
                    shipping_ammount: formatPrice(res.data.shipping_ammount),
                    tax_fee: formatPrice(res.data.tax_fee),
                    service_fee: formatPrice(res.data.service_fee),
                    saved: formatPrice(res.data.saved),
                    total: formatPrice(res.data.total)
                };
                setOrder(formattedOrder);
            })
            .catch(error => {
                console.error("Error fetching order:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error al cargar los detalles del pedido'
                });
            });
    };

    useEffect(() => {
        fetchOrderData();
    }, []);

    const applyCoupon = async () => {
        if (!order?.oid) return;

        const formdata = new FormData();
        formdata.append("order_oid", order.oid);
        formdata.append("coupon_code", couponCode);

        try {
            const response = await apiInstance.post("coupon/", formdata);
            fetchOrderData();
            Swal.fire({
                icon: response.data.icon,
                title: response.data.message
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error de Cupón',
                text: error.response?.data?.message || 'Error al aplicar el cupón'
            });
        }
    };

    const payWithStripe = async (event) => {
        event.preventDefault();
        if (!order?.oid) return;

        setPaymentLoading(true);

        try {
            const response = await apiInstance.post(`stripe-checkout/${order.oid}/`);
            
            if (response.data?.url) {
                // Persist session_id and order_oid as a fallback for the success page
                try {
                    const redirectUrl = new URL(response.data.url);
                    const sid = redirectUrl.searchParams.get('session_id') || redirectUrl.searchParams.get('sessionId');
                    if (sid) sessionStorage.setItem('last_stripe_session_id', sid);
                    if (order?.oid) sessionStorage.setItem('last_order_oid', order.oid);
                } catch (_) {}
                window.location.href = response.data.url;
            } else {
                throw new Error("No redirect URL received");
            }
        } catch (error) {
            console.error("Payment error:", error);
            Swal.fire({
                icon: 'error',
                title: 'Pago Fallido',
                text: error.response?.data?.error || 
                     'No se pudo conectar al servicio de pago'
            });
        } finally {
            setPaymentLoading(false);
        }
    };

    return (
        <div>
            <main className="mb-4 mt-4">
                <div className="container">
                    <section>
                        <div className="row gx-lg-5">
                            <div className="col-lg-8 mb-4 mb-md-0">
                                <section>
                                    <div className="alert alert-warning">
                                        <strong>Revisar los Detalles de Envío y Pedido</strong>
                                    </div>
                                    <form>
                                        <h5 className="mb-4 mt-4">Dirección de envío</h5>
                                        <div className="row mb-4">
                                            <div className="col-lg-12">
                                                <div className="form-outline">
                                                    <label className="form-label">Nombre Completo</label>
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="form-control"
                                                        value={order?.full_name || ''}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-lg-6 mt-4">
                                                <div className="form-outline">
                                                    <label className="form-label">Email</label>
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="form-control"
                                                        value={order?.email || ''}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-lg-6 mt-4">
                                                <div className="form-outline">
                                                    <label className="form-label">Móvil</label>
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="form-control"
                                                        value={order?.mobile || ''}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mt-4">
                                                <div className="form-outline">
                                                    <label className="form-label">Dirección</label>
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="form-control"
                                                        value={order?.address || ''}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mt-4">
                                                <div className="form-outline">
                                                    <label className="form-label">Ciudad</label>
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="form-control"
                                                        value={order?.city || ''}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mt-4">
                                                <div className="form-outline">
                                                    <label className="form-label">Estado</label>
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="form-control"
                                                        value={order?.state || ''}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mt-4">
                                                <div className="form-outline">
                                                    <label className="form-label">País</label>
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="form-control"
                                                        value={order?.country || ''}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <h5 className="mb-4 mt-4">Dirección de facturación</h5>
                                        <div className="form-check mb-2">
                                            <input 
                                                className="form-check-input me-2" 
                                                type="checkbox" 
                                                id="form6Example8" 
                                                defaultChecked 
                                            />
                                            <label className="form-check-label" htmlFor="form6Example8">
                                                Misma dirección de envío
                                            </label>
                                        </div>
                                    </form>
                                </section>
                            </div>

                            <div className="col-lg-4 mb-4 mb-md-0">
                                <section className="shadow-4 p-4 rounded-5 mb-4">
                                    <h5 className="mb-3">Resumen del Pedido</h5>
                                    <div className="d-flex justify-content-between mb-3">
                                        <span>Subtotal</span>
                                        <span>${order?.sub_total || '0.00'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Envío</span>
                                        <span>${order?.shipping_ammount || '0.00'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Impuesto</span>
                                        <span>${order?.tax_fee || '0.00'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Tarifa de Servicio</span>
                                        <span>${order?.service_fee || '0.00'}</span>
                                    </div>
                                    {order?.saved !== "0.00" && (
                                        <div className="d-flex text-danger fw-bold justify-content-between">
                                            <span>Descuento</span>
                                            <span>-${order?.saved || '0.00'}</span>
                                        </div>
                                    )}
                                    <hr className="my-4" />
                                    <div className="d-flex justify-content-between fw-bold mb-5">
                                        <span>Total</span>
                                        <span>${order?.total || '0.00'}</span>
                                    </div>

                                    <div className="shadow p-3 d-flex mt-4 mb-4">
                                        <input 
                                            onChange={(e) => setCouponCode(e.target.value)} 
                                            value={couponCode}
                                            type="text" 
                                            className='form-control' 
                                            style={{ border: "dashed 1px gray" }} 
                                            placeholder="Ingresar Código de Cupón" 
                                        />
                                        <button 
                                            onClick={applyCoupon} 
                                            className='btn btn-success ms-1'
                                            title="Aplicar cupón"
                                        >
                                            <i className='fas fa-check-circle'></i>
                                        </button>
                                    </div>
                                    
                                    {/* Payment Method - Stripe Only */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Método de Pago</label>
                                        <div className="form-check mb-2">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name="paymentMethod" 
                                                id="stripe" 
                                                value="stripe" 
                                                defaultChecked
                                                disabled
                                            />
                                            <label className="form-check-label" htmlFor="stripe">
                                                💳 Tarjeta de Crédito (Stripe) - Pagar Ahora
                                            </label>
                                        </div>
                                        <div className="alert alert-info mt-2" style={{ fontSize: '0.9rem' }}>
                                            <i className="fas fa-info-circle me-2"></i>
                                            <strong>Nota:</strong> Para pedidos por WhatsApp, por favor use el botón de WhatsApp en su carrito.
                                        </div>
                                    </div>

                                    {/* Stripe Payment Button */}
                                    <button 
                                        onClick={payWithStripe} 
                                        disabled={paymentLoading || !order?.oid}
                                        className="btn btn-primary btn-rounded w-100 mt-2" 
                                        style={{ backgroundColor: "#635BFF" }}
                                    >
                                        {paymentLoading ? (
                                            <>Procesando... <i className='fas fa-spinner fa-spin'></i></>
                                        ) : (
                                            <>Pagar Ahora (Stripe) <i className='fas fa-credit-card'></i></>
                                        )}
                                    </button>
                                </section>
                            </div>
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
}

export default Checkout;